export const COLORS = ["red", "orange", "green", "yellow", "blue", "pink"] as const;
export type ColorName = (typeof COLORS)[number];
export type PlannerMode = "settle" | "scan" | "enter" | "clip" | "approach";

export interface BundleFile {
  path: string;
  bytes: number;
  sha256: string;
}

export interface BundleManifest {
  schema: string;
  bytes: number;
  files: BundleFile[];
}

export interface ArraySpec {
  file: string;
  dtype: "<f4" | "|u1";
  shape: number[];
  bytes: number;
}

export interface ReferenceManifest {
  fps: number;
  frames: number;
  clip_names: string[];
  clip_offsets: number[];
  arrays: Record<string, ArraySpec>;
}

export interface ApproachWindowSpec {
  end: number;
  distance: number;
  travel_from_root: number;
}

export interface ApproachReferenceManifest {
  schema: string;
  source_sha256: string;
  fps: number;
  frames: number;
  windows: ApproachWindowSpec[];
  arrays: Record<string, ArraySpec>;
}

export interface ApproachReferenceData {
  manifest: ApproachReferenceManifest;
  jointPos: Float32Array;
  jointVel: Float32Array;
  pelvisQuat: Float32Array;
  pelvisLinVel: Float32Array;
  pelvisAngVel: Float32Array;
  contact: Uint8Array;
}

export interface ReferenceData {
  manifest: ReferenceManifest;
  jointPos: Float32Array;
  jointVel: Float32Array;
  pelvisQuat: Float32Array;
  pelvisLinVel: Float32Array;
  pelvisAngVel: Float32Array;
  contact: Uint8Array;
  approach?: ApproachReferenceData;
}

export interface PlannerConfig {
  belief_window: number;
  belief_min_votes: number;
  omega_still: number;
  pattern: string;
  retry_limit: number;
  arm_radius: number;
  horizon_gain: number;
  stance_band_m: number;
  scan_sweep_deg: number;
  settle_steps: number;
  scan_steps: number;
  hold_tail: number;
  blend_frames: number;
  lead_in_min_s: number;
  lead_in_max_s: number;
  enter_yaw_rate_deg: number;
  enter_joint_rate: number;
  still_slot: number;
  unified_transitions?: boolean;
  entry_match_velocity?: boolean;
  entry_hold_source_tilt?: boolean;
  entry_angular_velocity?: boolean;
  belief_scan_after_reads?: number;
  stateful_scan?: boolean;
  search_left_first?: boolean;
  search_left_each_cycle?: boolean;
  smooth_scan?: boolean;
  scan_yaw_rate_deg?: number;
  scan_yaw_accel_deg?: number;
  recovery_joint_rate?: number;
  recovery_min_s?: number;
  recovery_max_s?: number;
  recovery_transitions?: boolean;
  approach_enabled?: boolean;
  approach_enter_forward_m?: number;
  approach_enter_lateral_m?: number;
  approach_target_forward_m?: number;
  approach_max_step_m?: number;
  approach_turn_max_deg?: number;
  approach_turn_max_attempts?: number;
  approach_min_progress_m?: number;
  approach_max_attempts?: number;
  approach_no_progress_limit?: number;
  approach_escalate_window?: boolean;
  approach_forward_only?: boolean;
  approach_latch_blocked?: boolean;
  approach_fallback_to_clip?: boolean;
  approach_before_first_clip_only?: boolean;
}

export interface PlannerManifest {
  version: string;
  initial_target_color: number;
  half_extent: number;
  config: PlannerConfig;
}

export interface PlannerTable {
  clip: number[];
  entry: number[];
  exit: number[];
  delta: number[];
  clean: boolean[];
  qx: number[];
  qy: number[];
  qth: number[];
  ex_phi: number[];
  exit_range: number[];
  clip_end: number[];
  stand_frame: number;
  half_extent: number;
}

export interface PolicyManifest {
  run_path: string;
  control: { sim_timestep: number; decimation: number; step_dt: number };
  action: {
    joint_names: string[];
    scale: number[];
    default_joint_pos: number[];
    stiffness: number[];
    damping: number[];
  };
}

export interface EncoderManifest {
  input: { name: string; height: number; width: number };
  patch: { grid_h: number; grid_w: number };
  outputs: {
    patch_tokens: { num: number; dim: number };
    cls_token: { dim: number };
  };
}

export interface Plan {
  mode: PlannerMode;
  clip: number;
  entry: number;
  exit: number;
  label: string;
  delta: string;
  cost: number;
  hold: number;
  row: number;
  sym: number;
  yawOffset: number;
  entryYaw: number;
  matchedEntryYaw: number;
  entryTranslation: number;
  entryBearing: number;
  entryForward: number;
  entryLateral: number;
  approachWindow: number;
  approachRequested: number;
  approachCovered: number;
  approachTurn: boolean;
  searchScan: boolean;
  searchPhase: number;
}

export interface PlannerView {
  mode: PlannerMode;
  seenColor: number;
  targetColor: number;
  clipLabel: string;
  stagedTarget: number | null;
  solved: boolean;
  ladderPattern: string;
  ladderRung: number;
  expectedTurn: string;
}

export interface ReferenceHorizon {
  jointPos: Float32Array;
  jointVel: Float32Array;
  pelvisQuat: Float32Array;
  rootLinVel: Float32Array;
  rootAngVel: Float32Array;
  contact: Float32Array;
}
